import * as React from 'react'
import gql from 'graphql-tag'
import loadable from '@loadable/component'

import { useMutation } from '@apollo/react-hooks'
import {
  RateLimitBucket,
  useCaptcha,
  useLogger,
  useRequestConfig,
} from '@thg-commerce/enterprise-core'
import { MarketingType } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { FormItem, Input } from '@thg-commerce/gravity-elements'
import { DynamicForm } from '@thg-commerce/gravity-patterns'
import {
  EnterpriseThemeInterface,
  mq,
  styled,
  spacing,
} from '@thg-commerce/enterprise-theme'
import { pushToDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'

import { EmailEngagementModalProps, FormStatus } from './EmailEngagementModal'
import {
  Content,
  FormContainer,
  ImageContainer,
  InputContainer,
  ListContainer,
  ListItem,
  MessageContainer,
  PrivacyPolicy,
  StyledImage,
  SubmitButton,
  SubmittedText,
  SubmittedTitle,
  SuccessButton,
  TextContainer,
  Title,
} from './styles'
import { modalCallbacks } from './dataTracking'

const Tick = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/Tick'),
  {
    ssr: true,
    fallback: (
      <div style={{ width: '22px', height: '14px', display: 'inline-block' }} />
    ),
  },
)

interface SubmitValues {
  emailAddress: string
}

export const SIGNUP_FOR_MARKETING_MUTATION = gql`
  mutation signUpForMarketing($input: SignUpForMarketingInput!) {
    signUpForMarketing(input: $input) @client
  }
`

const TickIcon = styled(Tick)`
  margin-right: ${spacing(1)};
  stroke: ${(props) => props.theme.colors.palette.greys.darker};
  display: inline-block;
  width: 14px;
  height: 14px;
`

export const SuccessTick = styled(Tick)`
  width: 35px;
  height: 35px;
  stroke: ${(props) => props.theme.colors.palette.brand.base};
  display: block;
  margin: auto;
`

export const EmailEngagementModalContent = (
  props: EmailEngagementModalProps & {
    theme: EnterpriseThemeInterface
    i18nText: any
  },
) => {
  const logger = useLogger()
  const { csrf } = useRequestConfig()
  const captcha = useCaptcha({
    bucket: RateLimitBucket.MARKETING_MATERIAL_SIGN_UP,
  })

  const [addToNewsletter] = useMutation(SIGNUP_FOR_MARKETING_MUTATION)

  const bulletText = props.bulletText.map(
    (item, index) =>
      item && (
        <ListItem key={index}>
          <TickIcon />
          {item}
        </ListItem>
      ),
  )

  const signUpForMarketing = (
    values: SubmitValues,
    captchaResponse?: string,
  ) => {
    try {
      addToNewsletter({
        variables: {
          input: {
            type: MarketingType.Email,
            contactDetails: values.emailAddress,
            auditData: {
              messageShown: props.bulletText.join('\r\n'),
              formIdentifier: 'MARKETING_MATERIAL_SIGN_UP',
              formLocation: 'EmailEngagementModal',
            },
          },
        },
        context: {
          headers: {
            'X-Captcha-Type': captcha.type,
            'X-Captcha-Response': captchaResponse,
          },
        },
      }).then(({ data, errors }) => {
        if (
          data.signUpForMarketing &&
          !data.signUpForMarketing.errors &&
          !errors
        ) {
          pushToDataLayer({
            type: 'customEvent',
            eventData: {
              eventCategory: 'newsletter',
              eventAction: 'successful sign-up',
              eventLabel: '',
              eventPage: '',
            },
          })
          props.statusHandler(FormStatus.SUCCESSFUL)
        } else {
          logger.error('signupForMarketing error in Email Engagement Modal', {
            data,
            errors,
            'X-Captcha-Type': captcha.type,
            'X-Captcha-Response': captchaResponse,
          })
          props.statusHandler(FormStatus.FAILURE)
        }
      })
    } catch (err) {
      if (err instanceof Error) {
        logger.debug(err.message, {
          reqId: 'Email Engagment Modal',
          'X-Captcha-Type': captcha.type,
          'X-Captcha-Response': captchaResponse,
        })
      }
      props.statusHandler(FormStatus.FAILURE)
    }
  }

  const onSubmitHandler = (fields: SubmitValues) => {
    captcha.submit((response) => signUpForMarketing(fields, response))
  }

  return (
    <Content>
      <ImageContainer>
        <picture>
          <source
            media={mq(props.theme.breakpointUtils.map, 'lg', true)}
            srcSet={props.images.urls.large}
          />
          <source
            media={mq(props.theme.breakpointUtils.map, 'sm', true)}
            srcSet={props.images.urls.medium}
          />
          <StyledImage
            src={props.images.urls.small}
            alt={props.images.altText || ''}
            // @ts-ignore
            fetchpriority="high"
          />
        </picture>
      </ImageContainer>
      {props.status === FormStatus.UNSUBMITTED ? (
        <FormContainer>
          <ListContainer>
            <Title>{props.modalTitle}</Title>
            <TextContainer>{bulletText}</TextContainer>
          </ListContainer>
          <InputContainer>
            <DynamicForm
              csrf={csrf}
              onSubmit={onSubmitHandler}
              data-testid="form-guest-checkout"
              fields={[
                {
                  fieldName: 'emailAddress',
                  renderField: (
                    ref: React.RefObject<HTMLInputElement>,
                    key: string,
                  ) => (
                    <FormItem bindref={ref} disableSpacing key={key}>
                      <Input
                        label={props.i18nText.inputLabel}
                        required
                        type="email"
                        autocomplete="email"
                      />
                    </FormItem>
                  ),
                },
              ]}
            >
              {captcha.captcha}
              <SubmitButton
                type="submit"
                emphasis="high"
                data-testid="button-submit-newletter-signup"
              >
                {props.i18nText.submitButton}
              </SubmitButton>
            </DynamicForm>
            <PrivacyPolicy
              emphasis="low"
              renderedAs="a"
              href={`/${props.i18nText.privacyPolicyLink}`}
            >
              {props.i18nText.privacyPolicyText}
            </PrivacyPolicy>
          </InputContainer>
        </FormContainer>
      ) : props.status === FormStatus.SUCCESSFUL ? (
        <FormContainer>
          <MessageContainer>
            <SuccessTick viewBox="0 0 14 14" />
            <SubmittedTitle>{props.i18nText.successTitle}</SubmittedTitle>
            <SubmittedText>{props.i18nText.successText}</SubmittedText>
            <SuccessButton
              onClick={() => {
                props.closeHandler()
                modalCallbacks.continueButtonHandler()
              }}
              data-testid="continue-button-newsletter"
            >
              {props.i18nText.continueButton}
            </SuccessButton>
          </MessageContainer>
        </FormContainer>
      ) : (
        <FormContainer>
          <MessageContainer>
            <SubmittedTitle>{props.i18nText.errorTitle}</SubmittedTitle>
            <SubmittedText>{props.i18nText.errorText}</SubmittedText>
            <SuccessButton
              emphasis="low"
              onClick={() => {
                props.statusHandler(FormStatus.UNSUBMITTED)
              }}
              data-testid="retry-button-newsletter"
            >
              {props.i18nText.tryAgainButton}
            </SuccessButton>
          </MessageContainer>
        </FormContainer>
      )}
    </Content>
  )
}
